import type { CSSProperties, ReactNode } from 'react';
import { IconAlertTriangle } from '../atoms';

/**
 * LegalPage — the shared presentation shell for the standalone legal documents
 * (/legal/privacy, /legal/demo).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MAINTAINERS: PRE-COUNSEL DRAFT.
 *
 * Neither this shell nor the two documents that use it have been through
 * counsel. They exist so the demonstration instance is not shipped with NO
 * notice at all, which is the worse of the two failure modes. They are written
 * to be conservative and factually true of the code as it stands (see the
 * per-document notes in LegalPrivacy.tsx / LegalDemoNotice.tsx for what was
 * verified and what was deliberately not claimed).
 *
 * The split is deliberate so counsel REPLACES rather than merges: this file
 * owns layout only and contains no legal prose; each document file owns one
 * exported `LegalDocument` object that is the entire text. Swapping a policy is
 * "replace the const", not "diff the JSX".
 *
 * There is intentionally NO user-facing "DRAFT" watermark. A banner telling a
 * reader the notice isn't real invites them to disregard it, which protects
 * nobody and undermines the demo; the honest substance — that this is a
 * demonstration environment and these are not production terms — is stated
 * plainly in the documents themselves instead.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Deliberately dependency-light: no router hooks, no data fetching, no atoms —
 * plain anchors and CSS-variable tokens only. That means these pages render
 * outside the authenticated `App` shell (they are top-level routes in main.tsx,
 * alongside /login), are reachable pre-auth, and can be rendered in a bare test
 * without any provider. No hooks are used anywhere in this file, so there is no
 * Rules-of-Hooks surface to violate.
 */

/**
 * MAINTAINERS: confirm this address actually routes before the demo is shown to
 * anyone outside the team. It is the only contact route these documents offer.
 */
export const LEGAL_CONTACT_EMAIL = 'privacy@appealstack.com';

/** The date shown on both documents. Bump when the prose changes. */
export const LEGAL_LAST_UPDATED = 'August 4, 2026';

export interface LegalSection {
  heading: string;
  body: ReactNode;
}

export interface LegalDocument {
  /** Document title (the <h1>). */
  title: string;
  /** One-line description under the title. */
  summary: string;
  /** The standing callout above the body — the honest "what this is" line. */
  notice: ReactNode;
  /** Optional lead paragraph(s) before the numbered sections. */
  intro?: ReactNode;
  sections: LegalSection[];
}

/* ── Small content primitives, so a document file is prose and nothing else ── */

/** A paragraph. */
export function P({ children }: { children: ReactNode }) {
  return (
    <p style={{
      margin: '0 0 var(--sp-3)',
      fontSize: 'var(--fs-md)',
      lineHeight: 'var(--lh-relaxed)',
      color: 'var(--color-ink-2)',
    }}>{children}</p>
  );
}

/** A bulleted list. */
export function L({ items }: { items: ReactNode[] }) {
  return (
    <ul style={{
      margin: '0 0 var(--sp-3)',
      paddingLeft: 'var(--sp-5)',
      fontSize: 'var(--fs-md)',
      lineHeight: 'var(--lh-relaxed)',
      color: 'var(--color-ink-2)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--sp-2)',
    }}>
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
}

/**
 * A card grid — used for the service-provider list. `minmax(min(100%, 260px), 1fr)`
 * so a 320px viewport collapses to one column instead of overflowing sideways.
 */
export function Cards({ items }: { items: { name: string; body: ReactNode }[] }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
      gap: 'var(--sp-3)',
      margin: '0 0 var(--sp-3)',
    }}>
      {items.map((item) => (
        <div
          key={item.name}
          style={{
            padding: 'var(--sp-3) var(--sp-4)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-lg)',
            background: 'var(--color-card)',
            minWidth: 0,
          }}
        >
          <div style={{
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-ink)',
            marginBottom: 'var(--sp-1)',
          }}>{item.name}</div>
          <div style={{
            fontSize: 'var(--fs-sm)',
            lineHeight: 'var(--lh-relaxed)',
            color: 'var(--color-ink-3)',
            overflowWrap: 'anywhere',
          }}>{item.body}</div>
        </div>
      ))}
    </div>
  );
}

/** A mailto: link to the contact address. */
export function ContactLink() {
  return (
    <a
      href={`mailto:${LEGAL_CONTACT_EMAIL}`}
      style={{ color: 'var(--color-hunter)', overflowWrap: 'anywhere' }}
    >{LEGAL_CONTACT_EMAIL}</a>
  );
}

const linkStyle: CSSProperties = {
  color: 'var(--color-ink-3)',
  fontSize: 'var(--fs-sm)',
  textDecoration: 'none',
};

/**
 * Render a LegalDocument. Presentation only — every word on the page comes from
 * the `doc` argument.
 *
 * @param doc  the document to render
 * @param otherHref/otherLabel  the sibling legal document, cross-linked in the footer
 */
export function LegalPageShell({
  doc,
  otherHref,
  otherLabel,
}: {
  doc: LegalDocument;
  otherHref: string;
  otherLabel: string;
}) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg)',
      color: 'var(--color-ink)',
      // Horizontal padding stays small enough that a 320px viewport still has
      // usable line length; the inner column caps the width on desktop.
      padding: 'var(--sp-5) var(--sp-4) var(--sp-10)',
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <a href="/" style={{ ...linkStyle, display: 'inline-block', marginBottom: 'var(--sp-5)' }}>
          ← Back to AppealStack
        </a>

        <header style={{ marginBottom: 'var(--sp-5)' }}>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-2xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
            overflowWrap: 'break-word',
          }}>{doc.title}</h1>
          <p style={{
            margin: 'var(--sp-2) 0 0',
            fontSize: 'var(--fs-md)',
            color: 'var(--color-ink-3)',
            lineHeight: 'var(--lh-relaxed)',
          }}>{doc.summary}</p>
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
            Last updated {LEGAL_LAST_UPDATED}
          </p>
        </header>

        {/* Same visual language as StateRulesDisclaimer — an amber standing note
            at the point of reading, not a line buried in a contract. */}
        <div
          role="note"
          style={{
            display: 'flex',
            gap: 'var(--sp-3)',
            alignItems: 'flex-start',
            padding: 'var(--sp-3) var(--sp-4)',
            marginBottom: 'var(--sp-6)',
            background: 'var(--color-status-amber-bg)',
            border: '1px solid var(--color-status-amber)',
            borderRadius: 'var(--r-lg)',
          }}
        >
          {/* Line icon, not an emoji (part 4 §6). */}
          <IconAlertTriangle size={18} style={{ flexShrink: 0, color: 'var(--color-status-amber)' }} />
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', lineHeight: 'var(--lh-relaxed)' }}>
            {doc.notice}
          </div>
        </div>

        {doc.intro}

        {doc.sections.map((section, i) => (
          <section key={section.heading} style={{ marginBottom: 'var(--sp-6)' }}>
            <h2 style={{
              margin: '0 0 var(--sp-3)',
              fontSize: 'var(--fs-lg)',
              fontWeight: 'var(--fw-semibold)',
              letterSpacing: 'var(--ls-tight)',
              color: 'var(--color-ink)',
            }}>
              <span style={{ color: 'var(--color-muted)', fontWeight: 'var(--fw-normal)' }}>{i + 1}. </span>
              {section.heading}
            </h2>
            {section.body}
          </section>
        ))}

        <footer style={{
          marginTop: 'var(--sp-8)',
          paddingTop: 'var(--sp-4)',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--sp-2) var(--sp-4)',
        }}>
          <a href={otherHref} style={linkStyle}>{otherLabel}</a>
          <a href="/" style={linkStyle}>Back to AppealStack</a>
        </footer>
      </div>
    </div>
  );
}

/**
 * The footer link pair shown on sign-in and inside the portals. Plain anchors
 * (not react-router <Link>) on purpose: the legal pages live outside the
 * authenticated route tree, and an anchor keeps this component usable in shells
 * that are unit-tested without a Router.
 */
export function LegalFooterLinks({ style }: { style?: CSSProperties }) {
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 'var(--sp-2) var(--sp-4)',
      ...style,
    }}>
      <a href="/legal/demo" style={{ ...linkStyle, fontSize: 'var(--fs-xs)' }}>Demonstration notice</a>
      <a href="/legal/privacy" style={{ ...linkStyle, fontSize: 'var(--fs-xs)' }}>Privacy</a>
    </div>
  );
}
